import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import {
  ApplicationInsights,
  DistributedTracingModes,
  PageView,
  SeverityLevel,
} from '@microsoft/applicationinsights-web';

const reactPlugin = new ReactPlugin();

let appInsights: ApplicationInsights;

export const initApplicationInsights = (appCloudRoleName: string, applicationInsightsConnectionString: string) => {
  if (!applicationInsightsConnectionString) {
    console.log('No connection string, skipping initializing application insights');
    return appInsights;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString: applicationInsightsConnectionString,
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      distributedTracingMode: DistributedTracingModes.W3C,
      extensions: [reactPlugin],
    },
  });

  appInsights.loadAppInsights();
  // Intercept tracking to add / edit some metadata.
  appInsights.addTelemetryInitializer(function (envelope) {
    // Set cloud role name on all tracking
    if (envelope.tags) envelope.tags['ai.cloud.role'] = appCloudRoleName;

    // Don't add duplicated pageview events (reduce clutter)
    if (envelope.baseType === PageView.dataType && envelope.baseData?.refUri === envelope.baseData?.uri) {
      return false;
    }
  });

  return appInsights;
};

export const getReactPlugin = () => {
  return reactPlugin;
};

export const getAppInsights = () => {
  if (!appInsights) return;

  return appInsights;
};

export const addOrganizationMetaData = (partnerNumberOrganization: string | null) => {
  if (!appInsights) return;

  appInsights.addTelemetryInitializer(envelope => {
    if (envelope.data) envelope.data.partnerNumberOrganization = partnerNumberOrganization ?? 'multi';
  });
};

export const addAdminMetaData = (partnerNumberAdmin: string) => {
  if (!appInsights) return;

  appInsights.addTelemetryInitializer(envelope => {
    if (envelope.data) envelope.data.partnerNumberAdmin = partnerNumberAdmin;
  });
};

export const trackException = (error: Error, severityLevel?: SeverityLevel) => {
  if (!appInsights) return;

  appInsights.trackException({ error, severityLevel });
};

export const trackTrace = (message: string, severityLevel?: SeverityLevel) => {
  if (!appInsights) return;

  appInsights.trackTrace({ message: message, severityLevel });
};

/** Custom event; track user actions */
export const trackCustomEvent = (name: string, customDimensions?: { [key: string]: unknown }) => {
  if (!appInsights) return;

  appInsights.trackEvent({ name, properties: customDimensions });
};
